class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  // How long the closing animation in component-mega-menu.css runs for. The
  // panel is held open for exactly that long so it can animate out instead of
  // blinking away at the end of a slow, deliberate entrance.
  static CLOSE_DURATION = 220;

  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this.hoverMediaQuery = window.matchMedia('(min-width: 990px) and (hover: hover)');
    this.reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.addEventListener('mouseenter', this.onMouseEnter.bind(this));
    this.addEventListener('mouseleave', this.onMouseLeave.bind(this));
    this.mainDetailsToggle.querySelector('summary')?.addEventListener('click', this.onSummaryClick.bind(this));
  }

  // A click on a <summary> is swallowed by the details element — its activation
  // behaviour is "toggle", so a link nested inside it is not guaranteed to
  // navigate (and in some browsers does nothing at all). When the parent item
  // carries a real URL, take the plain left click and follow it ourselves.
  // Modified clicks (new tab/window, middle click) are left to the browser.
  onSummaryClick(event) {
    const link = event.target.closest('a[href]');
    if (!link || !this.contains(link)) return;
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
      return;

    // Without hover (a tablet wide enough for the inline menu), a tap is the
    // only way into the submenu, so it keeps toggling the panel — navigating
    // away instead would put the child links out of reach.
    if (!this.hoverMediaQuery.matches) {
      event.preventDefault();
      if (this.mainDetailsToggle.hasAttribute('open')) this.close();
      else this.open();
      return;
    }

    event.preventDefault();
    window.location.href = link.href;
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }

  onMouseEnter() {
    if (!this.hoverMediaQuery.matches) return;
    clearTimeout(this.hoverCloseTimeout);
    // Coming back before the panel has finished leaving cancels the exit and
    // hands the open panel straight back, rather than closing and re-running
    // the whole entrance.
    this.cancelClosing();
    this.open();
  }

  open() {
    if (this.mainDetailsToggle.hasAttribute('open')) return;
    this.mainDetailsToggle.setAttribute('open', '');
    this.mainDetailsToggle.querySelector('summary')?.setAttribute('aria-expanded', true);
  }

  onMouseLeave() {
    if (!this.hoverMediaQuery.matches) return;
    clearTimeout(this.hoverCloseTimeout);
    this.hoverCloseTimeout = setTimeout(() => this.close(), 150);
  }

  cancelClosing() {
    clearTimeout(this.closeAnimationTimeout);
    this.closeAnimationTimeout = undefined;
    this.mainDetailsToggle.classList.remove('mega-menu--closing');
  }

  // Keeps `open` on for the length of the exit animation, then closes for real.
  close() {
    const details = this.mainDetailsToggle;

    if (
      !details.classList.contains('mega-menu') ||
      !details.hasAttribute('open') ||
      this.reduceMotionQuery.matches
    ) {
      this.cancelClosing();
      super.close();
      return;
    }

    if (this.closeAnimationTimeout) return; // already leaving

    details.classList.add('mega-menu--closing');
    this.closeAnimationTimeout = setTimeout(() => {
      this.closeAnimationTimeout = undefined;
      details.classList.remove('mega-menu--closing');
      super.close();
    }, HeaderMenu.CLOSE_DURATION);
  }
}

customElements.define('header-menu', HeaderMenu);
